import { NAVIGATION_PADDING_MICROWU } from "../../assets/farm-asset-contract.ts";
import {
  distanceSquaredFromClosedAabb,
  isPointWalkable,
  isSegmentWalkable,
} from "./geometry.ts";
import { computeArrivalTicks, frameRouteFingerprint, sha256Hex } from "./fingerprint.ts";
import { findStablePath } from "./stable-a-star.ts";
import type {
  AssetObstacleMetadata,
  NavLayoutContract,
  NavSnapshot,
  ObstacleAabb,
  PointXZ,
  QueueSlot,
} from "./types.ts";

const NAV_REVISION = 1 as const;
const UNITS_PER_WU = 1000 as const;
const OBSTACLE_PADDING_MILLIWU = 300 as const;
const ENDPOINT_TOLERANCE_MILLIWU = 80 as const;
const FIRST_YARD_OUTER_POLYGON = [
  [-5200, -3500],
  [5200, -3500],
  [5200, 5400],
  [-5200, 5400],
] as const;

function invariant(condition: boolean, message: string): asserts condition {
  if (!condition) throw new Error(`invalid nav layout: ${message}`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function safeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && !Object.is(value, -0);
}

function assertPoint(value: unknown, label: string): asserts value is PointXZ {
  invariant(Array.isArray(value), `${label} must be a point`);
  invariant(value.length === 2, `${label} must have two coordinates`);
  invariant(safeInteger(value[0]) && safeInteger(value[1]), `${label} must use safe integer coordinates`);
}

function assertAabb(value: unknown, label: string): asserts value is ObstacleAabb {
  invariant(isRecord(value), `${label} is absent`);
  invariant(safeInteger(value.xMin), `${label}.xMin must be an integer`);
  invariant(safeInteger(value.xMax), `${label}.xMax must be an integer`);
  invariant(safeInteger(value.zMin), `${label}.zMin must be an integer`);
  invariant(safeInteger(value.zMax), `${label}.zMax must be an integer`);
  invariant(value.xMin < value.xMax && value.zMin < value.zMax, `${label} must have positive area`);
}

function assertUniqueIds(values: readonly Readonly<{ id: string }>[], label: string): void {
  const ids = new Set<string>();
  for (const value of values) {
    invariant(typeof value.id === "string" && value.id.length > 0, `${label} has an invalid id`);
    invariant(!ids.has(value.id), `${label} has a duplicate id`);
    ids.add(value.id);
  }
}

function findQueue(queues: readonly QueueSlot[], id: string): QueueSlot {
  const queue = queues.find((candidate) => candidate.id === id);
  invariant(queue !== undefined, `missing queue socket ${id}`);
  return queue;
}

function samePoint(left: readonly number[], right: readonly number[]): boolean {
  return left[0] === right[0] && left[1] === right[1];
}

function polylineLength(points: readonly PointXZ[]): number {
  let length = 0;
  for (let index = 1; index < points.length; index += 1) {
    const prior = points[index - 1]!;
    const current = points[index]!;
    length += Math.hypot(current[0] - prior[0], current[1] - prior[1]);
  }
  return length;
}

function deepFreeze<T>(value: T): Readonly<T> {
  if (isRecord(value) || Array.isArray(value)) {
    for (const key of Reflect.ownKeys(value)) deepFreeze((value as Record<PropertyKey, unknown>)[key]);
    Object.freeze(value);
  }
  return value;
}

function buildSnapshot(layout: NavLayoutContract): Readonly<NavSnapshot> {
  const copy = structuredClone(layout);
  return deepFreeze({
    contractId: copy.contract_id,
    revision: NAV_REVISION,
    unitsPerWU: UNITS_PER_WU,
    obstaclePaddingMilliWU: OBSTACLE_PADDING_MILLIWU,
    endpointToleranceMilliWU: ENDPOINT_TOLERANCE_MILLIWU,
    regions: copy.walkableRegions,
    obstacles: copy.obstacles,
    nodes: copy.nodes,
    actionApproaches: copy.actionApproaches,
    queueSlots: copy.queueSlots,
    recoveryAnchors: copy.recoveryAnchors,
    disabledPoints: copy.disabledUntilDynamicPublication,
    edges: copy.routeGraph.edges,
    arrivalProfiles: copy.arrivalProfiles,
  });
}

function validateQueues(layout: NavLayoutContract): void {
  const nest = layout.queueGeometryResolution.penNestPoint;
  assertPoint(nest, "pen nest point");
  invariant(samePoint(nest, [1000, -1273]), "pen queue geometry has a changed nest point");
  invariant(layout.queueGeometryResolution.minimumNestClearanceWU === "1.265898890", "pen queue geometry has a changed minimum clearance");
  const penExpected = [
    ["chicken-pen-01-q1", [-250, -1473], 1_602_500, "1.265898890"],
    ["chicken-pen-01-q2", [-250, -823], 1_765_000, "1.328533026"],
    ["chicken-pen-01-q3", [-250, -2123], 2_285_000, "1.511621646"],
  ] as const;
  for (const expected of penExpected) {
    const queue = findQueue(layout.queueSlots, expected[0]);
    invariant(
      samePoint(queue.point, expected[1]) &&
        queue.distanceFromNestSquaredMilliWU2 === expected[2] &&
        queue.distanceFromNestWU === expected[3],
      "pen queue geometry differs from the authored values",
    );
    const dx = queue.point[0] - nest[0];
    const dz = queue.point[1] - nest[1];
    invariant(dx * dx + dz * dz === expected[2], "pen queue geometry has an inconsistent distance");
  }
  const q1 = findQueue(layout.queueSlots, "roadside-market-01-q1");
  const q2 = findQueue(layout.queueSlots, "roadside-market-01-q2");
  invariant(
    samePoint(q1.point, [-801, 1945]) &&
      q1.distanceFromServiceMilliWU === 1250 &&
      samePoint(q2.point, [-411, 1425]) &&
      q2.distanceFromServiceMilliWU === 1900 &&
      q2.distanceFromPriorQueueMilliWU === 650,
    "market queue geometry differs from the authored values",
  );
  invariant(Math.hypot(q2.point[0] - q1.point[0], q2.point[1] - q1.point[1]) === 650, "market queue geometry has inconsistent spacing");
}

function validateFingerprints(layout: NavLayoutContract): void {
  invariant(layout.goldenDirectRoutes.length === layout.routeGraph.edges.length, "golden direct route count differs from edge count");
  invariant(layout.routeFingerprint.expectedFingerprints.length === 20, "forward fingerprint count must be 20");
  invariant(layout.routeFingerprint.expectedReverseFingerprints.length === 20, "reverse fingerprint count must be 20");
  for (const golden of layout.goldenDirectRoutes) {
    const edge = layout.routeGraph.edges.find((candidate) => candidate.id === golden.edgeId);
    invariant(edge !== undefined, `golden route references unknown edge ${golden.edgeId}`);
    invariant(edge.from === golden.from && edge.to === golden.to && edge.costMilliWU === golden.costMilliWU, `golden route ${golden.edgeId} is inconsistent`);
    for (const profileId of ["player-v1", "generalist-v1"] as const) {
      const profile = layout.arrivalProfiles[profileId];
      invariant(profile !== undefined, `missing arrival profile ${profileId}`);
      const arrivalTicks = computeArrivalTicks(edge.costMilliWU, profile);
      invariant(golden.arrivalTicks[profileId] === arrivalTicks, `arrival ticks differ for ${edge.id}/${profileId}`);
      const forward = layout.routeFingerprint.expectedFingerprints.find(
        (vector) => vector.edgeId === edge.id && vector.profileId === profileId,
      );
      const reverse = layout.routeFingerprint.expectedReverseFingerprints.find(
        (vector) => vector.edgeId === edge.id && vector.profileId === profileId,
      );
      invariant(forward !== undefined && reverse !== undefined, `missing route fingerprint for ${edge.id}/${profileId}`);
      const common = { revision: 1, totalCostMilliWU: edge.costMilliWU, profileId, arrivalTicks } as const;
      invariant(
        sha256Hex(frameRouteFingerprint({ ...common, fromNodeId: edge.from, toNodeId: edge.to, waypoints: edge.polyline })) === forward.sha256,
        `forward fingerprint differs for ${edge.id}/${profileId}`,
      );
      invariant(
        sha256Hex(frameRouteFingerprint({ ...common, fromNodeId: edge.to, toNodeId: edge.from, waypoints: [...edge.polyline].reverse() })) === reverse.sha256,
        `reverse fingerprint differs for ${edge.id}/${profileId}`,
      );
    }
  }
}

export function validateNavLayout(value: unknown): Readonly<NavSnapshot> {
  invariant(isRecord(value), "contract must be a record");
  const layout = value as unknown as NavLayoutContract;
  invariant(layout.contract_id === "NT-I0-FIRST-YARD-NAV-V1", "contract id is not authoritative");
  invariant(layout.version === 1, "contract version must be 1");
  invariant(layout.units?.worldUnitsToInteger === UNITS_PER_WU, "world units must use 1000 integer units");
  invariant(Array.isArray(layout.walkableRegions) && layout.walkableRegions.length === 1, "exactly one walkable region is required");
  invariant(Array.isArray(layout.obstacles) && layout.obstacles.length === 2, "exactly two obstacles are required");
  invariant(Array.isArray(layout.nodes) && layout.nodes.length === 7, "exactly seven nodes are required");
  invariant(Array.isArray(layout.actionApproaches) && layout.actionApproaches.length === 8, "exactly eight action approaches are required");
  invariant(Array.isArray(layout.queueSlots) && layout.queueSlots.length === 5, "exactly five queue slots are required");
  invariant(Array.isArray(layout.recoveryAnchors) && layout.recoveryAnchors.length === 3, "exactly three recovery anchors are required");
  invariant(Array.isArray(layout.disabledUntilDynamicPublication) && layout.disabledUntilDynamicPublication.length === 1, "exactly one disabled dynamic point is required");
  invariant(isRecord(layout.routeGraph) && Array.isArray(layout.routeGraph.edges) && layout.routeGraph.edges.length === 10, "exactly ten graph edges are required");
  invariant(layout.routeGraph.directed === false && layout.routeGraph.heuristic === "zero", "graph must use undirected zero-heuristic search");

  const region = layout.walkableRegions[0]!;
  invariant(region.enabled && Array.isArray(region.outerPolygonCCW) && region.outerPolygonCCW.length >= 3, "walkable region is invalid");
  for (const point of region.outerPolygonCCW) assertPoint(point, "outer polygon point");
  invariant(
    region.outerPolygonCCW.length === FIRST_YARD_OUTER_POLYGON.length &&
      region.outerPolygonCCW.every((point, index) => samePoint(point, FIRST_YARD_OUTER_POLYGON[index]!)),
    "outer polygon differs from the frozen first-yard boundary",
  );
  assertUniqueIds(layout.obstacles, "obstacles");
  for (const obstacle of layout.obstacles) {
    assertAabb(obstacle.rawObstacleAabb, `${obstacle.id} raw footprint`);
    assertAabb(obstacle.paddedObstacleFootprint, `${obstacle.id} padded footprint`);
    const raw = obstacle.rawObstacleAabb;
    const padded = obstacle.paddedObstacleFootprint;
    invariant(
      obstacle.paddingAppliedMilliWU === OBSTACLE_PADDING_MILLIWU &&
        padded.xMin === raw.xMin - OBSTACLE_PADDING_MILLIWU &&
        padded.xMax === raw.xMax + OBSTACLE_PADDING_MILLIWU &&
        padded.zMin === raw.zMin - OBSTACLE_PADDING_MILLIWU &&
        padded.zMax === raw.zMax + OBSTACLE_PADDING_MILLIWU,
      `${obstacle.id} must use one-time 300-milliWU padding`,
    );
    invariant(region.subtractPaddedObstacleIds.includes(obstacle.id), `${obstacle.id} is not subtracted from the region`);
  }

  assertUniqueIds(layout.nodes, "nodes");
  assertUniqueIds(layout.actionApproaches, "action approaches");
  assertUniqueIds(layout.queueSlots, "queue slots");
  assertUniqueIds(layout.recoveryAnchors, "recovery anchors");
  assertUniqueIds(layout.routeGraph.edges, "route edges");
  const snapshot = buildSnapshot(layout);
  const enabledPoints = [
    ...layout.nodes.map((entry) => entry.point),
    ...layout.actionApproaches.map((entry) => entry.point),
    ...layout.queueSlots.map((entry) => entry.point),
    ...layout.recoveryAnchors.map((entry) => entry.point),
  ];
  invariant(enabledPoints.length === 23, "enabled point count must be 23");
  for (const point of enabledPoints) {
    assertPoint(point, "enabled point");
    invariant(isPointWalkable(snapshot, point), "enabled point must be walkable");
  }
  for (const disabled of layout.disabledUntilDynamicPublication) assertPoint(disabled.point, "disabled point");

  for (const node of layout.nodes) {
    invariant(node.enabled && !node.obstructing, `node ${node.id} must be enabled and non-obstructing`);
    invariant(layout.actionApproaches.some((approach) => approach.nodeId === node.id), `node ${node.id} is missing an approach socket`);
  }
  for (const approach of layout.actionApproaches) {
    invariant(layout.nodes.some((node) => node.id === approach.nodeId), `approach ${approach.id} references an unknown node`);
    for (const obstacle of layout.obstacles) {
      invariant(distanceSquaredFromClosedAabb(approach.point, obstacle.paddedObstacleFootprint) >= 90_000, `approach ${approach.id} violates 300-milliWU clearance`);
    }
  }
  for (const queue of layout.queueSlots) invariant(layout.nodes.some((node) => node.id === queue.nodeId), `queue ${queue.id} references an unknown node`);
  validateQueues(layout);

  const nodeIds = layout.nodes.map((node) => node.id);
  for (const edge of layout.routeGraph.edges) {
    const from = layout.nodes.find((node) => node.id === edge.from);
    const to = layout.nodes.find((node) => node.id === edge.to);
    invariant(from !== undefined && to !== undefined, `edge ${edge.id} references an unknown node`);
    invariant(safeInteger(edge.costMilliWU) && edge.costMilliWU > 0, `edge ${edge.id} has an invalid integer cost`);
    invariant(Array.isArray(edge.polyline) && edge.polyline.length >= 2, `edge ${edge.id} has an invalid polyline`);
    for (const point of edge.polyline) assertPoint(point, `${edge.id} polyline point`);
    invariant(samePoint(edge.polyline[0]!, from.point) && samePoint(edge.polyline[edge.polyline.length - 1]!, to.point), `edge ${edge.id} endpoints differ from nodes`);
    for (let index = 1; index < edge.polyline.length; index += 1) {
      invariant(isSegmentWalkable(snapshot, edge.polyline[index - 1]!, edge.polyline[index]!), `edge ${edge.id} has a blocked segment`);
    }
    invariant(Math.abs(polylineLength(edge.polyline) - edge.costMilliWU) <= 20, `edge ${edge.id} polyline length differs from cost`);
  }
  const origin = nodeIds[0]!;
  for (const destination of nodeIds) invariant(findStablePath(nodeIds, layout.routeGraph.edges, origin, destination) !== null, "route graph must be connected");
  validateFingerprints(layout);
  return snapshot;
}

export function assertAssetObstacleCompatibility(
  snapshot: Readonly<NavSnapshot>,
  obstacleId: string,
  metadata: Readonly<AssetObstacleMetadata>,
): true {
  const obstacle = snapshot.obstacles.find((candidate) => candidate.id === obstacleId);
  invariant(obstacle !== undefined, `unknown nav obstacle ${obstacleId}`);
  const raw = metadata.rawObstacleAabbMicroWU;
  const padded = metadata.paddedObstacleFootprintMicroWU;
  const compatible =
    metadata.paddingAppliedMicroWU === NAVIGATION_PADDING_MICROWU &&
    raw.min.x === obstacle.rawObstacleAabb.xMin * 1000 && raw.max.x === obstacle.rawObstacleAabb.xMax * 1000 &&
    raw.min.z === obstacle.rawObstacleAabb.zMin * 1000 && raw.max.z === obstacle.rawObstacleAabb.zMax * 1000 &&
    padded.minX === obstacle.paddedObstacleFootprint.xMin * 1000 && padded.maxX === obstacle.paddedObstacleFootprint.xMax * 1000 &&
    padded.minZ === obstacle.paddedObstacleFootprint.zMin * 1000 && padded.maxZ === obstacle.paddedObstacleFootprint.zMax * 1000;
  invariant(compatible, "asset obstacle metadata is incompatible with the authored nav obstacle");
  return true;
}
